import {useEffect, useState, useContext, useCallback, useRef, useMemo} from "react";
import {useNavigate, useParams} from "react-router";
import styled, {css} from "styled-components";
import {DragDropContext, Droppable} from "react-beautiful-dnd";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {
  faEdit,
  faFilter,
  faHistory,
  faMapMarkerAlt,
  faPlus,
  faSearch,
  faSun
} from "@fortawesome/free-solid-svg-icons";
import PropTypes from "prop-types";

// Hooks
import useMountedState from "../hooks/useMountedState.js";
import useApi from "../hooks/useApi.js";

// Contexts
import {FacilityNavContext} from "../contexts/facilitynav.js";
import {AuthContext} from "../contexts/auth.js";
import {NavContext} from "../contexts/nav.js";
import {useSocket} from "../contexts/socket.js";

// Utils
import {exists, toTitleCase} from "../utils/helpers.js";

// Helpers
import {
  GROUP,
  COMPONENT,
  FIELD,
  addGroup,
  addComponent,
  addField,
  removeElement,
  updateElement,
  deepCopyBuilder,
  applyElementPosition
} from "../utils/builder.js";
import MapProvider, {
  DEFAULT_MARKER,
  FACILITY_MARKER,
  formatAddressComponents,
  formatAddressForGeocode
} from "../utils/google/maps.js";
import filterFacilityBuilder from "./facility-builder/filterBuilder.js";

// Components
import FacilityPageHeader from "./general/FacilityPageHeader.js";
import Map from "../components/Map.js";
import RenderFacilityBuilder from "./facility-builder/RenderFacilityBuilder.js";
import Badge from "../components/Badge.js";
import MultiSelect from "../components/MultiSelect.js";
import PrimaryAddress from "./facility/PrimaryAddress.js";
import Details from "./facility/Details.js";
import Modal from "../components/Modal.js";
import ModalSetLocation from "./facility-builder/ModalSetLocation.js";
import ModalAddField from "./general/ModalAddField.js";
import ModalEditElement from "./facility-builder/ModalEditElement.js";
import ModalDeleteElement from "./facility-builder/ModalDeleteElement.js";
import ModalLocation from "./facility-builder/ModalLocation.js";
import ModalFacilityHistory from "./facility-builder/ModalFacilityHistory.js";
import ModalBuilderVersionNote from "./general/ModalBuilderVersionNote.js";
import ModalElementDisabledHistory from "./checksheet-builder/ModalElementDisabledHistory.js";
import ModalFormula from "./general/ModalFormula.js";
import ModalRedirect from "./general/ModalRedirect.js";
import ModalConditionals from "./checksheet-builder/ModalConditionals.js";
import ModalEditFacility from "./facility/ModalEditDetails.js";
import ModalWeatherTable from "./facility/ModalWeatherTable.js";

// Style
import {bp} from "../style/components/breakpoints.js";
import {voice} from "../style/components/typography.js";
import {z} from "../style/components/mixins.js";
import {
  border,
  radius,
  pad,
  navWidth,
  navHeight,
  shadow,
  colors
} from "../style/components/variables.js";
import {
  Page,
  Loader,
  NotLoaded,
  SearchIcon,
  SearchWrapper,
  Search,
  Inline,
  StickyWrapper,
  Button,
  Error,
  Small,
  HeadingMedium,
  HeadingCenter,
  Text,
  List,
  ListItem
} from "../style/components/general.js";

// Socket Constants
import Room, {LIST_LOCKS, LOCK, RELEASE, ROOM_JOINED} from "./general/Room.js";

const NOTIFY_UPDATE = "notify_update_builder";
const LISTEN_NOTIFY_UPDATE = "notify:update_builder";

const maps = new MapProvider();

const FacilityBuilder = ({sidecar = false}) => {
  const isMounted = useMountedState();

  const navigate = useNavigate();

  const socket = useSocket();

  const {slug} = useParams();

  const {api: apiFacilities} = useApi("facilities");
  const {api: apiAddress} = useApi("addresses");
  const {api: apiUnits} = useApi("units");

  const {currentUser, atLeast, roleCanAccessResource} = useContext(AuthContext);
  const {facility, setFacility} = useContext(FacilityNavContext);
  const {setIntermediateAction} = useContext(NavContext);

  const [checksheets, setChecksheets] = useState();
  const [locks, setLocks] = useState();
  const [active, setActive] = useState();
  const [builder, setBuilder] = useState();
  const [storedBuilderUpdate, setStoredBuilderUpdate] = useState();
  const [builderQueryResult, setBuilderQueryResult] = useState();
  const [query, setQuery] = useState("");
  const [refreshResults, setRefreshResults] = useState(false);
  const [refreshMarkers, setRefreshMarkers] = useState(false);
  const [activeDroppableId, setActiveDroppableId] = useState("");
  const [view, setView] = useState(roleCanAccessResource("facility", "view") ? "builder" : "map");
  const [bulkEdit, setBulkEdit] = useState(false);
  const [showSaveWarning, setShowSaveWarning] = useState(false);
  const [showAddWarning, setShowAddWarning] = useState(false);
  const [showUpdateWarning, setShowUpdateWarning] = useState(false);
  const [queuedAdd, setQueuedAdd] = useState();
  const [queuedUpdate, setQueuedUpdate] = useState();
  const [queuedAction, setQueuedAction] = useState();
  const [units, setUnits] = useState([]);
  const [markers, setMarkers] = useState([]);
  // Map
  const [locations, setLocations] = useState();
  const [activeMarker, setActiveMarker] = useState("");
  const [filters, setFilters] = useState([]);
  const [activeFilters, setActiveFilters] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [showWeatherModal, setShowWeatherModal] = useState(false);
  // Modals
  const [target, setTarget] = useState();
  const [showModalSharedField, setShowModalSharedField] = useState(false);
  const [formulaMode, setFormulaMode] = useState("create");
  const [showModalRedirectFormula, setShowModalRedirectFormula] = useState(false);
  const [showModalFormula, setShowModalFormula] = useState(false);
  const [showModalEdit, setShowModalEdit] = useState(false);
  const [showModalDelete, setShowModalDelete] = useState(false);
  const [showModalLocation, setShowModalLocation] = useState(false);
  const [showPrimaryLocation, setShowPrimaryLocation] = useState(false);
  const [showModalLocationSet, setShowModalLocationSet] = useState(false);
  const [showModalHistory, setShowModalHistory] = useState(false);
  const [showModalHistoryNote, setShowModalHistoryNote] = useState(false);
  const [showModalStateHistory, setShowModalStateHistory] = useState(false);
  const [showModalConditional, setShowModalConditional] = useState(false);
  const [showModalDetails, setShowModalDetails] = useState(false);

  const checksheetChanges = useRef([]);
  const targetName = useRef();
  const bulkEditRef = useRef(false);

  useEffect(() => {
    bulkEditRef.current = bulkEdit;
  }, [bulkEdit]);

  const hasPublishedChecksheets = useMemo(
    () => checksheets?.filter(({draft}) => !draft)?.length > 0,
    [checksheets]
  );

  const markerIconMap = useMemo(() => {
    const map = {};
    markers?.map(({id, ...rest}) => {
      map[id] = {...rest};
    });
    return map;
  }, [markers]);

  /**
   * Refresh Google Map Markers
   * @param {array} a Addresses
   * @param {object} b Facility Builder
   */
  const loadMap = useCallback(
    (a, b) => {
      const addresses = a ? [...a] : [];
      const primary = facility?.primaryAddress;
      if (
        a &&
        primary &&
        exists(primary.lat) &&
        exists(primary.lon) &&
        !addresses.some(address => String(address.id) === String(primary.id))
      )
        addresses.push({...primary, primary: true});

      let extracted = [
        ...addresses.map(address => ({label: address.name, ...address, marker: FACILITY_MARKER})),
        ...Object.values(b?.byId || {})
          .filter(({address, deleted}) => !deleted && exists(address?.lat) && exists(address?.lon))
          .map(({id, name, label, address, help, markerId}) => ({
            label,
            ...address,
            id: id || name,
            details: help ? [...help] : [{}],
            draggable: true,
            marker:
              markerId && Object.keys(markerIconMap)?.length > 0 && markerId in markerIconMap
                ? {id: markerId, ...markerIconMap[markerId]}
                : DEFAULT_MARKER
          }))
      ];

      if (activeFilters?.length > 0)
        extracted = extracted.filter(({marker}) => activeFilters.includes(marker.id));

      setLocations(extracted);
      setRefreshMarkers(true);
    },
    [markerIconMap, activeFilters, facility?.primaryAddress]
  );

  useEffect(() => {
    if (facility?.builder && !bulkEditRef.current) {
      setBuilder(facility.builder);
      if (targetName.current) {
        const element = facility.builder.byId[targetName.current];
        if (element) setTarget(element);
        else {
          setTarget(undefined);
          targetName.current = undefined;
          setShowModalStateHistory(false);
        }
      }
    }
  }, [facility]);

  const loadFacility = useCallback(() => {
    apiFacilities
      .callGet(slug, {withChecksheets: true, withDraftChecksheets: true})
      .then(({status, data}) => {
        if (status === 200 && data) {
          if (data.checksheets?.length) setChecksheets([...data.checksheets]);
          setFacility(data);
          setBuilder(data.builder);
        }
      });
  }, [apiFacilities, slug, setFacility]);

  // Initial load
  useEffect(() => {
    if (isMounted() && !locations) {
      apiAddress.callGet(null, {markers: true}).then(({status: s, data}) => {
        if (s === 200 && data) setMarkers(data);
      });
      apiUnits.callGet().then(({status: s, data}) => {
        if (s === 200 && data) setUnits(data);
      });
      loadFacility();
    }
  }, [isMounted, locations, apiAddress, apiUnits, loadFacility]);

  useEffect(() => {
    if (isMounted() && facility && builder)
      loadMap(
        builderQueryResult ? null : facility.addresses,
        builderQueryResult ?? builder,
        !!builderQueryResult
      );
  }, [isMounted, loadMap, facility, builder, builderQueryResult]);

  useEffect(() => {
    if (view === "builder") setActiveMarker();
  }, [view]);

  const promptForSave = useCallback(action => {
    setQueuedAction(() => action);
    setShowSaveWarning(true);
  }, []);

  useEffect(() => {
    if (sidecar) return undefined;

    if (bulkEdit) setIntermediateAction(() => promptForSave);
    else setIntermediateAction(undefined);

    const handleBeforeUnload = e => {
      if (bulkEdit) {
        e.preventDefault();
        e.returnValue = "";
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      setIntermediateAction(undefined);
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [bulkEdit, sidecar, promptForSave, setIntermediateAction]);

  const notifyUpdate = useCallback(
    ({publicId}) => {
      if (publicId !== currentUser.publicId && !bulkEdit) loadFacility();
    },
    [currentUser, loadFacility, bulkEdit]
  );

  const listLocks = useCallback(({lockList}) => setLocks(lockList), []);

  const roomJoined = useCallback(({lockList}) => setLocks(lockList), []);

  // Socket Management
  useEffect(() => {
    socket.on(LIST_LOCKS, listLocks);
    socket.on(ROOM_JOINED, roomJoined);
    socket.on(LISTEN_NOTIFY_UPDATE, notifyUpdate);

    return () => {
      // unbind all event handlers used in this component
      socket.off(LIST_LOCKS, listLocks);
      socket.off(LISTEN_NOTIFY_UPDATE, notifyUpdate);
      socket.off(ROOM_JOINED, roomJoined);
    };
  }, [socket, isMounted, listLocks, roomJoined, currentUser, locks, slug, notifyUpdate]);

  const searchAndFilter = useCallback(() => {
    if ((activeFilters.length === 0 && !query) || !builder) setBuilderQueryResult();
    else
      setBuilderQueryResult(
        filterFacilityBuilder(builder, {
          query,
          activeFilters,
          markerIconMap,
          keepParentsOfMatches: true
        })
      );
  }, [activeFilters, builder, markerIconMap, query]);

  useEffect(() => {
    if (refreshResults) setRefreshResults(false);
    searchAndFilter();
  }, [builder, query, refreshResults, searchAndFilter, activeFilters]);

  useEffect(() => {
    if (!activeMarker || !locations) return;
    if (
      (activeFilters.length > 0 || (query && query !== "")) &&
      !locations.some(location => String(location.id) === String(activeMarker))
    )
      setActiveMarker(null);
  }, [activeFilters, query, locations, activeMarker]);

  // Browser Resize Events
  const handleResize = useCallback(
    localView => {
      if (window.innerWidth >= 1024) setView(undefined);
      else if (localView === null)
        setView(roleCanAccessResource("facility", "view") ? "builder" : "map");
    },
    [roleCanAccessResource]
  );

  const handleResizeTimeout = useCallback(
    (resized, localView) => {
      clearTimeout(resized);
      resized = setTimeout(() => handleResize(localView), 10);
    },
    [handleResize]
  );

  useEffect(() => {
    let resizedFn;
    const fnWrapper = () => handleResizeTimeout(resizedFn, view);
    if (isMounted() && locations?.length > 0) {
      window.removeEventListener("resize", fnWrapper);
      if (!view || window.innerWidth >= 1024) handleResize();

      window.addEventListener("resize", fnWrapper);
    }

    return () => {
      window.removeEventListener("resize", fnWrapper);
    };
  }, [isMounted, view, locations, handleResize, handleResizeTimeout]);

  // Updates
  const handleSave = useCallback(
    (updatedBuilder, note) => {
      const params = {
        builder: updatedBuilder,
        details: facility.details,
        note,
        bulkEdit
      };

      if (checksheetChanges.current?.length) params.changes = checksheetChanges.current;
      apiFacilities.callPut(facility.id, params).then(({status, data}) => {
        if (target) {
          socket.emit(RELEASE, `${slug}-builder`, target.name);
          setTarget(undefined);
          targetName.current = undefined;
        }

        if (status === 200) {
          socket.emit(NOTIFY_UPDATE, `${slug}-builder`, currentUser.publicId);
          // Reset
          setRefreshResults(true);
          loadMap(facility.addresses, updatedBuilder);
          checksheetChanges.current = [];

          if (data?.facility) setFacility(data.facility);
          if (data?.facility?.checksheets) setChecksheets(data.facility.checksheets);
        }
      });
    },
    [apiFacilities, bulkEdit, currentUser, facility, loadMap, setFacility, slug, socket, target]
  );

  const handleSaveElement = useCallback(
    (targetElement, updatedBuilder = builder, update = false) => {
      setQuery("");
      setFilters([]);
      setActiveFilters([]);

      const {label, linkedParent} = targetElement;

      // Add parent name and remove temporary key
      if (targetElement.linkedParent) targetElement.parentName = targetElement.linkedParent;
      delete targetElement.linkedParent;

      // Get all parent element children
      const matches = Object.values(builder.byId).filter(
        ({label: targetLabel, parentName: targetParent, element}) =>
          (element === COMPONENT &&
            label.toUpperCase().trim() === targetLabel?.trim() &&
            targetParent === linkedParent) ||
          (element === GROUP &&
            label.toUpperCase().trim() === targetLabel.trim() &&
            !linkedParent) ||
          (element === FIELD && label.toUpperCase().trim() === targetLabel.trim())
      );

      let updated = deepCopyBuilder(updatedBuilder);

      if (matches?.length && updated.byId[matches?.[0]]?.deleted) {
        const [match] = matches;

        if (
          checksheets?.some(({builder: checksheetBuilder}) => match.name in checksheetBuilder.byId)
        ) {
          setQueuedAdd(match);
          setShowAddWarning(true);
          return;
        }

        delete updated.byId[match.name].deleted;
      } else {
        if (targetElement.element === GROUP) updated = addGroup(updated, targetElement);
        if (targetElement.element === COMPONENT) updated = addComponent(updated, targetElement);
        if (targetElement.element === FIELD && !update) {
          const nameMap = {};
          // Use name map with fake existing name to get new generated key
          updated = addField(updated, {...targetElement, shared: true}, nameMap, "new");
        }
        if (targetElement.element === FIELD && update) {
          // Use name map with fake existing name to get new generated key
          ({builder: updated} = updateElement(updated, {...targetElement, shared: true}, false));
          checksheetChanges.current = [
            ...checksheetChanges.current,
            {
              action: "shared",
              field: {...targetElement}
            }
          ];
        }
      }

      setTarget(undefined);
      targetName.current = undefined;

      if (bulkEdit) {
        setBuilder(updated);
        loadMap(facility.addresses, updated);
      } else if (hasPublishedChecksheets) {
        setStoredBuilderUpdate(updated);
        setShowModalHistoryNote(true);
      } else {
        setBuilder(updated);
        handleSave(updated);
      }
    },
    [builder, checksheets, facility, hasPublishedChecksheets, loadMap, bulkEdit, handleSave]
  );

  const handleAdd = useCallback(
    element => {
      if (element.element === FIELD) {
        setTarget(element);
        targetName.current = element.name;
        setShowModalSharedField(true);
      } else handleSaveElement(element);
    },
    [handleSaveElement]
  );

  const handleDelete = useCallback(
    element => {
      socket.emit(LOCK, `${slug}-builder`, element.name);
      setShowModalDelete(true);
      setTarget(element);
      targetName.current = element.name;
    },
    [slug, socket]
  );

  const handleNote = useCallback(
    note => {
      const copiedBuilder = deepCopyBuilder(storedBuilderUpdate);
      setBuilder(copiedBuilder);
      handleSave(copiedBuilder, note);
      setShowModalHistoryNote(false);
      setStoredBuilderUpdate(undefined);
      setBulkEdit(false);
    },
    [handleSave, storedBuilderUpdate]
  );

  const handleBulk = useCallback(() => {
    setStoredBuilderUpdate(builder);
    setShowModalHistoryNote(true);
  }, [builder]);

  const handleInitiateBulk = useCallback(() => {
    setBuilder(storedBuilderUpdate);
    setBulkEdit(true);
    setStoredBuilderUpdate(undefined);
  }, [storedBuilderUpdate]);

  const handleEditLocation = async (coords, id) => {
    const element = builder.byId[id];
    if (element)
      await maps.geocode({latLng: coords}, results => {
        for (let i = 0; i < results.length; i++) {
          if (results[i].types[0] === "street_address") {
            const address = formatAddressComponents(results[i].address_components);
            if (address) {
              const {builder: updatedBuilder} = updateElement(
                builder,
                {
                  ...element,
                  id: id,
                  address: {...address, lat: coords.lat(), lon: coords.lng()}
                },
                false
              );

              handleSave(updatedBuilder);
              setTarget(undefined);
              targetName.current = undefined;
            }

            break;
          }
        }
      });
  };

  const persistLocation = useCallback(
    async coords => {
      const formatted = maps.getLatLng(coords.lat, coords.lon);
      setLocations(undefined);
      let updatedBuilder;
      await maps.geocode({latLng: formatted}, results => {
        let locationFound = false;
        if (results?.length > 0)
          for (let i = 0; i < results.length; i++) {
            if (results[i].types[0] === "street_address") {
              const address = formatAddressComponents(results[i].address_components);
              if (address) {
                locationFound = true;
                ({builder: updatedBuilder} = updateElement(
                  builder,
                  {
                    ...target,
                    id: target.name,
                    address: {...address, ...coords}
                  },
                  false
                ));
                break;
              }
            }
          }

        if (!locationFound)
          ({builder: updatedBuilder} = updateElement(
            builder,
            {
              ...target,
              id: target.name,
              address: {
                id: target.name,
                ...coords
              }
            },
            false
          ));

        handleSave(updatedBuilder);
        // Reset
        setRefreshResults(true);
        setTarget(undefined);
        targetName.current = undefined;
        loadMap(facility.addresses, updatedBuilder);
      });
    },
    [builder, target, loadMap, facility, handleSave]
  );

  const handleEdit = useCallback(
    element => {
      socket.emit(LOCK, `${slug}-builder`, element.name);
      setTarget(element);

      targetName.current = element.name;
      setShowModalEdit(true);
    },
    [slug, socket, setTarget]
  );

  const condFormFilter = useCallback(
    (update = queuedUpdate?.[0]) =>
      ({builder: checksheetBuilder}) => {
        if (update.name in checksheetBuilder.byId) {
          const {condition, depends, trueDepends} = update;

          if (
            condition?.list?.length &&
            condition.list.some(
              ({depends: condDepends}) =>
                condDepends in facility.builder.byId && !(condDepends in checksheetBuilder.byId)
            )
          )
            return true;

          if (
            depends?.length &&
            depends.some(dep => dep in facility.builder.byId && !(dep in checksheetBuilder.byId))
          )
            return true;

          if (
            trueDepends?.length &&
            trueDepends.some(
              dep => dep in facility.builder.byId && !(dep in checksheetBuilder.byId)
            )
          )
            return true;
        }
        return false;
      },
    [facility, queuedUpdate]
  );

  const handleFormula = element => {
    setTarget(element);
    targetName.current = element.name;
    if (element.type === "generated") setShowModalRedirectFormula(true);
    else setShowModalFormula(true);
  };

  const handleConditional = element => {
    setTarget(element);
    targetName.current = element.name;
    setShowModalConditional(true);
  };

  const saveFormula = useCallback(
    (field, updatedBuilder) => {
      if (field.type === "generated")
        if (checksheets?.filter(condFormFilter(field))?.length) {
          setQueuedUpdate([field, updatedBuilder]);
          setShowUpdateWarning(true);
        } else {
          handleSaveElement(field, updatedBuilder, true);
        }
      else
        handleSaveElement(
          {
            ...field,
            element: "field",
            type: "generated",
            disabled: true
          },
          updatedBuilder
        );
    },
    [checksheets, condFormFilter, handleSaveElement]
  );

  const saveConditional = useCallback(
    values => {
      const update = {
        ...target,
        condition: values.condition
      };

      if (checksheets?.filter(condFormFilter(update))?.length) {
        setQueuedUpdate([update, storedBuilderUpdate]);
        setShowUpdateWarning(true);
      } else handleSaveElement(update, storedBuilderUpdate, true);
    },
    [checksheets, condFormFilter, handleSaveElement, storedBuilderUpdate, target]
  );

  const handleSetLocation = element => {
    setTarget(element);
    targetName.current = element.name;
    setShowModalLocationSet(true);
  };

  const onDragStart = provided => {
    const {droppableId} = provided.source;
    setActiveDroppableId(droppableId);
  };

  // re-ordering list
  const onDragEnd = useCallback(
    result => {
      const {destination, source, type} = result;
      if (!destination) {
        return;
      }

      let updatedBuilder;
      const builderCopy = {...builder};

      if (type === "DEFAULT") {
        const allIds = [...builder.allIds];

        const [removed] = allIds.splice(source.index, 1);
        allIds.splice(destination.index, 0, removed);

        updatedBuilder = {...builderCopy, allIds};
      } else if (type === source.droppableId) {
        const byId = {...builder.byId};

        const parent = byId[type];
        const {children} = parent;

        const [removed] = children.splice(source.index, 1);
        children.splice(destination.index, 0, removed);

        updatedBuilder = {...builderCopy, byId: {...byId, [type]: {...parent, children}}};
      }

      if (bulkEdit) {
        setBuilder(updatedBuilder);
        loadMap(facility.addresses, updatedBuilder);
      } else if (hasPublishedChecksheets) {
        setStoredBuilderUpdate(updatedBuilder);
        setShowModalHistoryNote(true);
      } else {
        setBuilder(updatedBuilder);
        handleSave(updatedBuilder);
      }
    },
    [builder, bulkEdit, facility, handleSave, hasPublishedChecksheets, loadMap]
  );

  const handleSearch = e => {
    const search = e.target.value;
    setQuery(search);
  };

  const filterMenu = useRef(null);

  useEffect(() => {
    const handleClickOutsideFilterMenu = e =>
      filterMenu?.current !== null &&
      !filterMenu.current.contains(e.target) &&
      !filterMenu.current.contains(e.target.nextSibling) &&
      setShowFilters(false);

    if (filterMenu) document.addEventListener("mousedown", handleClickOutsideFilterMenu);

    // Cleanup
    return () => document.removeEventListener("mousedown", handleClickOutsideFilterMenu);
  }, [filterMenu]);

  const handleApply = () => {
    setShowFilters(false);
    setActiveFilters(filters);
  };

  const handleClear = () => {
    setFilters([]);
    setActiveFilters([]);
    setShowFilters(false);
  };

  const persistEdit = useCallback(
    async element => {
      setShowModalEdit(false);

      const {position, ...elementToSave} = element;

      const typeChange = elementToSave?.type
        ? checksheets?.length > 0 && elementToSave.type !== builder.byId[elementToSave.name]?.type
        : false;

      if (
        builder.byId[target.name].parentName !== elementToSave.parentName &&
        checksheets?.some(({builder: checksheetBuilder}) => target.name in checksheetBuilder.byId)
      ) {
        const temp = checksheetChanges.current ? [...checksheetChanges.current] : [];
        temp.push({action: "reassign", name: target.name});
        checksheetChanges.current = temp;
      }

      const copiedBuilder = deepCopyBuilder(builder);
      let updatedBuilder = copiedBuilder;

      const nameMap = {};
      if (elementToSave?.address && !elementToSave.address?.line1) {
        const coords = maps.getLatLng(elementToSave.address.lat, elementToSave.address.lon);
        await maps.geocode({latLng: coords}, results => {
          let locationFound = false;

          if (results?.length > 0)
            for (let i = 0; i < results.length; i++) {
              if (results[i].types[0] === "street_address") {
                const address = formatAddressComponents(results[i].address_components);
                if (address) {
                  locationFound = true;
                  ({builder: updatedBuilder} = updateElement(
                    copiedBuilder,
                    {
                      ...elementToSave,
                      address: {
                        id: elementToSave.name,
                        ...address,
                        lat: coords.lat(),
                        lon: coords.lng()
                      }
                    },
                    false,
                    nameMap,
                    typeChange,
                    typeChange
                  ));
                  break;
                }
              }
            }

          if (!locationFound) {
            ({builder: updatedBuilder} = updateElement(
              copiedBuilder,
              {
                ...elementToSave,
                address: {
                  id: elementToSave.name,
                  lat: coords.lat(),
                  lon: coords.lng()
                }
              },
              false,
              nameMap,
              typeChange,
              typeChange
            ));
          }
        });
      } else if (
        elementToSave?.address &&
        (!elementToSave.address.lat || !elementToSave?.address.lon)
      ) {
        await maps.geocode({address: formatAddressForGeocode(elementToSave.address)}, results => {
          if (results && results.length > 0) {
            // Should provide a single match
            const address = formatAddressComponents(results[0].address_components);
            if (address) {
              ({builder: updatedBuilder} = updateElement(
                copiedBuilder,
                {
                  ...elementToSave,
                  address: {
                    id: elementToSave.name,
                    ...address,
                    lat: results[0].geometry.location.lat(),
                    lon: results[0].geometry.location.lng()
                  }
                },
                false,
                nameMap,
                typeChange,
                typeChange
              ));
            }
          }
        });
      } else {
        ({builder: updatedBuilder} = updateElement(
          copiedBuilder,
          elementToSave,
          false,
          nameMap,
          typeChange,
          typeChange
        ));
      }

      if (typeChange) {
        handleClear();
        checksheetChanges.current = [
          ...checksheetChanges.current,
          {
            action: "shared",
            field: {...elementToSave, name: nameMap[elementToSave.name]}
          }
        ];
      }

      if (elementToSave.element === GROUP || elementToSave.element === COMPONENT) {
        const savedName = nameMap[elementToSave.name] || elementToSave.name;
        updatedBuilder = applyElementPosition(updatedBuilder, savedName, position);
      }

      if (bulkEdit) {
        setBuilder(updatedBuilder);
        loadMap(facility.addresses, updatedBuilder);
      } else if (hasPublishedChecksheets) {
        setStoredBuilderUpdate(updatedBuilder);
        setShowModalHistoryNote(true);
      } else {
        setBuilder(updatedBuilder);
        handleSave(updatedBuilder);
      }
    },
    [builder, bulkEdit, checksheets, facility, handleSave, hasPublishedChecksheets, loadMap, target]
  );

  const persistDelete = useCallback(() => {
    setShowModalDelete(false);

    const copiedBuilder = deepCopyBuilder(builder);

    if (
      checksheets?.some(({builder: checksheetBuilder}) => target.name in checksheetBuilder.byId)
    ) {
      const temp = checksheetChanges.current ? [...checksheetChanges.current] : [];
      temp.push({action: "delete", name: target.name});
      checksheetChanges.current = temp;
    }

    const updatedBuilder = removeElement(copiedBuilder, target, true);

    if (bulkEdit) {
      setBuilder(updatedBuilder);
      loadMap(facility.addresses, updatedBuilder);
    } else if (hasPublishedChecksheets) {
      setStoredBuilderUpdate(updatedBuilder);
      setShowModalHistoryNote(true);
    } else {
      setBuilder(updatedBuilder);
      handleSave(updatedBuilder);
    }
  }, [
    builder,
    bulkEdit,
    checksheets,
    facility,
    handleSave,
    hasPublishedChecksheets,
    loadMap,
    target
  ]);

  const handleShowModalStateHistory = useCallback(
    name => {
      setTarget(builder.byId[name]);
      targetName.current = name;

      setShowModalStateHistory(true);
    },
    [builder]
  );

  const facilityBuilderRendered = useMemo(
    () => (
      <>
        <DragDropContext onDragStart={onDragStart} onDragEnd={onDragEnd}>
          <Droppable
            droppableId="facility.builder"
            isDropDisabled={activeDroppableId !== "facility.builder"}>
            {provided => (
              <div {...provided.droppableProps} ref={provided.innerRef}>
                <RenderFacilityBuilder
                  facility={facility}
                  builder={builderQueryResult || builder}
                  handleFormula={handleFormula}
                  handleConditional={handleConditional}
                  handleAdd={handleAdd}
                  removeElement={handleDelete}
                  editElement={handleEdit}
                  setElementLocation={handleSetLocation}
                  handleSave={handleSave}
                  activeDroppableId={activeDroppableId}
                  querying={!!query && query !== ""}
                  activeMarker={activeMarker}
                  setActiveMarker={setActiveMarker}
                  markerIconMap={markerIconMap}
                  visibleLocationIds={
                    (activeFilters.length > 0 || (query && query !== "")) && locations
                      ? locations.map(location => location.id)
                      : null
                  }
                  active={active}
                  locks={locks}
                  view={view}
                  setView={setView}
                  handleBulk={bulkEdit && !showModalHistoryNote ? handleBulk : undefined}
                  handleShowModalStateHistory={handleShowModalStateHistory}>
                  {provided.placeholder}
                </RenderFacilityBuilder>
              </div>
            )}
          </Droppable>
        </DragDropContext>

        {showModalSharedField && target && (
          <ModalAddField
            visible={showModalSharedField}
            setVisible={setShowModalSharedField}
            disallowed={[
              "text",
              "textarea",
              "checkbox",
              "confirm",
              "multiselect",
              "switch",
              "radio",
              "date",
              "time",
              "dropdown",
              "upload"
            ]}
            parent={builder.byId[target.linkedParent]}
            builder={builder}
            units={units}
            single
            label={target.label}
            addFields={fields =>
              handleSaveElement({
                ...fields[0],
                element: FIELD,
                children: [],
                shared: true
              })
            }
          />
        )}

        {showModalFormula && target && (
          <ModalFormula
            visible={showModalFormula}
            setVisible={state => {
              if (!state) setFormulaMode("create");
              setShowModalFormula(state);
            }}
            targetElement={target}
            mode={formulaMode}
            page="builder"
            builder={builder}
            units={units}
            hasPrimaryAddress={!!facility?.primaryAddress}
            save={saveFormula}
            inFacilityBuilder
          />
        )}

        {showModalConditional && target && (
          <ModalConditionals
            visible={showModalConditional && !!target}
            setVisible={state => {
              if (!state) {
                setTarget(undefined);
                targetName.current = undefined;
              }
              setShowModalConditional(state);
            }}
            targetElement={target}
            save={saveConditional}
            builder={builder}
            hasPrimaryAddress={!!facility?.primaryAddress}
          />
        )}

        {showModalEdit && target && (
          <ModalEditElement
            visible={showModalEdit}
            setVisible={val => {
              socket.emit(RELEASE, `${slug}-builder`, target.name);
              setShowModalEdit(val);
            }}
            targetElement={target}
            setTargetElement={el => {
              setTarget(el);
              targetName.current = el.name;
            }}
            persistEdit={persistEdit}
            builder={builder}
            markerIconMap={markerIconMap}
            checksheets={checksheets}
            units={units}
            saveWarningAction={
              showSaveWarning
                ? confirm => {
                    if (confirm) {
                      if (typeof queuedAction === "string") navigate(queuedAction);
                      else if (typeof queuedAction === "function") queuedAction();
                    } else {
                      setQueuedAction(undefined);
                      setShowSaveWarning(false);
                    }
                  }
                : undefined
            }
          />
        )}

        {showModalDelete && target && (
          <ModalDeleteElement
            builder={builder}
            visible={showModalDelete}
            setVisible={setShowModalDelete}
            facilityId={facility?.id}
            persistDelete={persistDelete}
            targetElement={target}
            checksheets={checksheets}
            saveWarningAction={
              showSaveWarning
                ? confirm => {
                    if (confirm) {
                      if (typeof queuedAction === "string") navigate(queuedAction);
                      else if (typeof queuedAction === "function") queuedAction();
                    } else {
                      setQueuedAction(undefined);
                      setShowSaveWarning(false);
                    }
                  }
                : undefined
            }
          />
        )}

        {showModalLocation && (
          <ModalLocation
            visible={showModalLocation}
            setVisible={state => {
              if (!state) setShowPrimaryLocation(false);
              setShowModalLocation(state);
            }}
            loadMap={loadMap}
            initialAddress={showPrimaryLocation ? facility?.primaryAddress?.id : undefined}
          />
        )}

        {showModalLocationSet && target && (
          <ModalSetLocation
            visible={showModalLocationSet}
            setVisible={setShowModalLocationSet}
            persistEdit={persistLocation}
            targetElement={target}
            setTargetElement={el => {
              setTarget(el);
              targetName.current = el.name;
            }}
          />
        )}

        {showModalHistory && (
          <ModalFacilityHistory
            visible={showModalHistory}
            setVisible={setShowModalHistory}
            facility={facility}
          />
        )}

        {showModalHistoryNote && (
          <ModalBuilderVersionNote
            visible={showModalHistoryNote}
            setVisible={state => {
              if (!state) setStoredBuilderUpdate(undefined);

              setShowModalHistoryNote(false);
            }}
            submitNote={handleNote}
            cancel={() => {
              setStoredBuilderUpdate(undefined);
              setShowModalHistoryNote(false);
            }}
            handleInitiateBulk={!bulkEdit ? handleInitiateBulk : undefined}
          />
        )}

        {showModalStateHistory && target && (
          <ModalElementDisabledHistory
            visible={showModalStateHistory && !!target}
            setVisible={state => {
              if (!state) {
                setTarget(undefined);
                targetName.current = undefined;
              }
              setShowModalStateHistory(state);
            }}
            target={target}
          />
        )}

        {showModalRedirectFormula && (
          <ModalRedirect
            visible={showModalRedirectFormula}
            setVisible={setShowModalRedirectFormula}
            buttonOptions={[
              {
                action: () => {
                  setFormulaMode("create");
                  setShowModalFormula(true);
                  setShowModalRedirectFormula(false);
                },
                description: "Create formula based on this field",
                icon: faPlus
              },
              {
                action: () => {
                  setFormulaMode("edit");
                  setShowModalFormula(true);
                  setShowModalRedirectFormula(false);
                },
                description: "Edit existing formula field",
                icon: faEdit
              }
            ]}
          />
        )}

        {showSaveWarning && !(showModalEdit || showModalDelete) && (
          <Modal
            visible={showSaveWarning}
            setVisible={state => {
              if (!state) setQueuedAction(undefined);
              setShowSaveWarning(state);
            }}>
            <HeadingCenter>Discard Changes?</HeadingCenter>
            <Text>
              Are you sure you want to leave this page? You have unsaved changes that will be lost.
            </Text>
            <br />
            <Inline>
              <WarningButton
                onClick={() => {
                  if (typeof queuedAction === "string") navigate(queuedAction);
                  else if (typeof queuedAction === "function") queuedAction();
                }}>
                Continue and Discard
              </WarningButton>
              <Button
                onClick={() => {
                  setQueuedAction(undefined);
                  setShowSaveWarning(false);
                }}>
                Cancel
              </Button>
            </Inline>
          </Modal>
        )}

        {showAddWarning && (
          <Modal
            visible={showAddWarning}
            setVisible={state => {
              if (!state) setQueuedAdd(undefined);
              setShowAddWarning(false);
            }}>
            <HeadingCenter>Restore {toTitleCase(queuedAdd.element)}?</HeadingCenter>
            <Text>
              This {queuedAdd.element} will be added back to the checksheets it was originally used
              in:
            </Text>
            <br />
            {checksheets && (
              <List column>
                {checksheets
                  ?.filter(
                    ({builder: checksheetBuilder}) => queuedAdd.name in checksheetBuilder.byId
                  )
                  ?.map(({id, name, frequency}) => (
                    <ListItem key={`checksheet-${id}`}>
                      {name}
                      {frequency?.name ? ` (${frequency.name.toUpperCase()})` : ""}
                    </ListItem>
                  ))}
              </List>
            )}
            <br />
            <Inline>
              <SuccessButton
                onClick={() => {
                  const updatedBuilder = deepCopyBuilder(builder);

                  const temp = checksheetChanges.current ? [...checksheetChanges.current] : [];
                  temp.push({action: "restore", name: queuedAdd.name});
                  checksheetChanges.current = temp;
                  delete updatedBuilder.byId[queuedAdd.name].deleted;

                  if (bulkEdit) {
                    setBuilder(updatedBuilder);
                    loadMap(facility.addresses, updatedBuilder);
                  } else if (hasPublishedChecksheets) {
                    setStoredBuilderUpdate(updatedBuilder);
                    setShowModalHistoryNote(true);
                  } else {
                    setBuilder(updatedBuilder);
                    handleSave(updatedBuilder);
                  }

                  setQueuedAdd(undefined);
                  setShowAddWarning(false);
                }}>
                Confirm
              </SuccessButton>
              <Button
                onClick={() => {
                  setQueuedAdd(undefined);
                  setShowAddWarning(false);
                }}>
                Cancel
              </Button>
            </Inline>
          </Modal>
        )}

        {showUpdateWarning && (
          <Modal
            visible={showUpdateWarning}
            setVisible={state => {
              if (!state) setQueuedUpdate(undefined);
              setShowUpdateWarning(false);
            }}>
            <HeadingCenter>Update Field?</HeadingCenter>
            <Text>
              This field is used in the following checksheets, and any related fields will be added
              to them if not already present:
            </Text>
            <br />
            {checksheets && (
              <List column>
                {checksheets?.filter(condFormFilter())?.map(({id, name, frequency}) => (
                  <ListItem key={`checksheet-${id}`}>
                    {name}
                    {frequency?.name ? ` (${frequency.name.toUpperCase()})` : ""}
                  </ListItem>
                ))}
              </List>
            )}
            <br />
            <Inline>
              <SuccessButton
                onClick={() => {
                  handleSaveElement(queuedUpdate[0], queuedUpdate[1], true);

                  setQueuedUpdate(undefined);
                  setShowUpdateWarning(false);
                }}>
                Confirm
              </SuccessButton>
              <Button
                onClick={() => {
                  setQueuedUpdate(undefined);
                  setShowUpdateWarning(false);
                }}>
                Cancel
              </Button>
            </Inline>
          </Modal>
        )}
      </>
    ),
    [
      active,
      activeDroppableId,
      activeMarker,
      activeFilters,
      builder,
      builderQueryResult,
      bulkEdit,
      checksheets,
      condFormFilter,
      facility,
      formulaMode,
      handleAdd,
      handleBulk,
      handleDelete,
      handleEdit,
      handleInitiateBulk,
      handleNote,
      handleSave,
      handleSaveElement,
      handleShowModalStateHistory,
      hasPublishedChecksheets,
      loadMap,
      locations,
      locks,
      markerIconMap,
      showPrimaryLocation,
      navigate,
      onDragEnd,
      persistDelete,
      persistEdit,
      persistLocation,
      query,
      queuedAction,
      queuedAdd,
      queuedUpdate,
      saveConditional,
      saveFormula,
      showAddWarning,
      showModalConditional,
      showModalDelete,
      showModalEdit,
      showModalFormula,
      showModalHistory,
      showModalHistoryNote,
      showModalLocation,
      showModalLocationSet,
      showModalRedirectFormula,
      showModalSharedField,
      showModalStateHistory,
      showSaveWarning,
      showUpdateWarning,
      slug,
      socket,
      target,
      units,
      view
    ]
  );

  const shouldRenderDetails = useMemo(
    () =>
      activeFilters?.length === 0 &&
      facility?.details &&
      !facility.type &&
      Object.values(facility.details).some(item => item) &&
      roleCanAccessResource("facility_information", "view"),
    [activeFilters, facility, roleCanAccessResource]
  );

  if (sidecar) return facilityBuilderRendered;

  return (
    <BuilderPage hasMenu>
      <Room name="builder" active={active} setActive={setActive} withLocks />

      {facility?.name && (
        <FacilityPageHeader
          facility={facility}
          reloadData={() => setLocations(undefined)}
          path="/builder"
        />
      )}

      <PageMenu>
        <HeadingMedium>
          Facility&nbsp;
          {atLeast("creator") && roleCanAccessResource("facility", "create") ? "Builder" : "Map"}
        </HeadingMedium>
        {roleCanAccessResource("facility", "view") && (
          <ViewSwitch
            onClick={() => {
              setView(view === "builder" ? "map" : "builder");
            }}
            general={!atLeast("creator")}>
            {view === "builder" ? "View Map" : "View Builder"}
          </ViewSwitch>
        )}
      </PageMenu>

      <Content fullWidth>
        {roleCanAccessResource("facility", "view") && (
          <Builder data-testid="facility.preview" view={view}>
            <BuilderOptions>
              {roleCanAccessResource("facility_address", "view") && (
                <Button type="button" onClick={() => setShowModalLocation(true)}>
                  Location(s)
                </Button>
              )}
              <Inline>
                <SearchWrapper>
                  <SearchIcon>
                    <FontAwesomeIcon icon={faSearch} />
                  </SearchIcon>
                  <Search
                    name="search"
                    type="text"
                    placeholder="Filter..."
                    onChange={e => handleSearch(e)}
                  />
                </SearchWrapper>
                <WeatherButton type="button" onClick={() => setShowWeatherModal(true)}>
                  <FontAwesomeIcon icon={faSun} />
                </WeatherButton>
                {markers?.length > 0 && (
                  <Filters>
                    <Button type="button" title="Filters" onClick={() => setShowFilters(true)}>
                      <Badge
                        count={activeFilters?.length}
                        title="Active filters"
                        offset="14px"
                        color={colors.heroGreen}
                      />
                      <FontAwesomeIcon icon={faFilter} />
                    </Button>
                    {showFilters && (
                      <OptionSub ref={filterMenu}>
                        <Small>Filter(s)</Small>
                        <MultiSelect
                          options={[
                            {...DEFAULT_MARKER, value: DEFAULT_MARKER.id},
                            ...markers.map(({id, label, icon, color}) => ({
                              value: `${id}`,
                              label,
                              icon,
                              color
                            }))
                          ]}
                          setSelection={setFilters}
                          defaultSelection={filters}
                        />
                        <Inline>
                          <ApplyFilter type="button" onClick={handleApply}>
                            Apply
                          </ApplyFilter>
                          <ApplyFilter type="button" onClick={handleClear}>
                            Clear
                          </ApplyFilter>
                        </Inline>
                      </OptionSub>
                    )}
                  </Filters>
                )}
                <Button
                  type="button"
                  title="Facility Builder History"
                  onClick={() => setShowModalHistory(true)}>
                  <FontAwesomeIcon icon={faHistory} />
                </Button>
              </Inline>
            </BuilderOptions>

            {(!locations || locations.length === 0) && (
              <WarningText>
                {!!query && query !== ""
                  ? "No locations found"
                  : "Update group locations to view in Facility Map."}
              </WarningText>
            )}

            {shouldRenderDetails && (
              <DetailWrapper>
                <Details
                  details={{
                    region: facility.region?.name?.toUpperCase(),
                    ...facility.details,
                    address: facility.primaryAddress
                  }}
                  control={
                    roleCanAccessResource("facility_information", "update") &&
                    !facility.isDeleted && (
                      <Edit type="button" onClick={() => setShowModalDetails(true)}>
                        <FontAwesomeIcon icon={faEdit} />
                      </Edit>
                    )
                  }
                />
              </DetailWrapper>
            )}

            {activeFilters?.length === 0 &&
              facility?.primaryAddress &&
              roleCanAccessResource("facility_address", "view") && (
                <DetailWrapper>
                  <PrimaryAddress details={{...facility.details, address: facility.primaryAddress}}>
                    <View
                      type="button"
                      onClick={() => {
                        setActiveMarker(facility.primaryAddress.id);
                        if (view && setView) setView("map");
                      }}>
                      View on Map
                      <Icon icon={faMapMarkerAlt} color={`#${FACILITY_MARKER.color}`} />
                    </View>
                    {roleCanAccessResource("facility_address", "update") && !facility.isDeleted && (
                      <AddressEdit
                        type="button"
                        onClick={() => {
                          setShowPrimaryLocation(true);
                          setShowModalLocation(true);
                        }}>
                        <FontAwesomeIcon icon={faEdit} />
                      </AddressEdit>
                    )}
                  </PrimaryAddress>
                </DetailWrapper>
              )}

            {builder && locks ? (
              facilityBuilderRendered
            ) : (
              <NotLoaded>
                <Loader data-testid="loader.facilityBuilder" />
              </NotLoaded>
            )}
          </Builder>
        )}

        <MapWrapper view={view} empty={!locations || locations.length === 0}>
          {locations ? (
            <Map
              locations={locations}
              editLocation={handleEditLocation}
              activeMarker={activeMarker}
              setActiveMarker={setActiveMarker}
              filters={activeFilters}
              refreshMarkers={refreshMarkers}
              querying={!!query && query !== ""}
              setRefreshMarkers={setRefreshMarkers}
            />
          ) : (
            <Loader />
          )}
        </MapWrapper>
      </Content>

      {showModalDetails && (
        <ModalEditFacility
          visible={showModalDetails}
          setVisible={setShowModalDetails}
          facility={facility}
          setFacility={setFacility}
        />
      )}

      {showWeatherModal && (
        <ModalWeatherTable
          visible={showWeatherModal}
          setVisible={setShowWeatherModal}
          facility={facility}
        />
      )}
    </BuilderPage>
  );
};

FacilityBuilder.propTypes = {
  sidecar: PropTypes.bool
};

// Style Overrides
const BuilderPage = styled(Page)`
  align-items: flex-start;
  display: block;
  height: 100%;
  margin-bottom: ${pad * 2}px;

  ${bp(4)} {
    display: flex;
    flex-wrap: wrap;
    width: calc(100% - ${navWidth}px);
  }
`;

const Content = styled(Inline)`
  gap: ${pad * 2}px;
  align-items: start;
`;

const PageMenu = styled(Inline)`
  width: 100%;
  padding: 0 0 ${pad}px 0;
  align-items: center;
  justify-content: space-between;
`;

const BuilderOptions = styled(Inline)`
  width: 100%;
  justify-content: space-between;
`;

const WeatherButton = styled(Button)`
  display: none;

  ${bp(2)} {
    display: block;
  }
`;

const ViewSwitch = styled(Button)`
  ${bp(4)} {
    display: none;
    pointer-events: none;
  }
`;

const Builder = styled.div`
  position: relative;
  width: inherit;
  border: ${border} solid ${({theme}) => theme.secondary};
  border-radius: ${radius};
  padding: ${pad * 2}px;

  ${bp(4)} {
    margin: 0;
    display: block;
    width: 40%;
  }

  ${({view}) =>
    view === "builder"
      ? css`
          display: block;
        `
      : css`
          display: none;
        `};
`;

const MapWrapper = styled(StickyWrapper)`
  width: 100%;
  height: calc(55vh - (${({empty}) => (empty ? "30px" : "0px")}));
  margin: 0 0 ${navHeight}px;

  ${bp(4)} {
    display: block;
    width: 60%;
    height: calc(75vh - (${({empty}) => (empty ? "30px" : "0px")}));
    top: calc(${navHeight}px + ${pad * 2}px);
  }

  ${({view}) =>
    view === "map"
      ? css`
          display: block;
        `
      : css`
          display: none;
        `};
`;

const WarningText = styled(Error)`
  color: ${({theme}) => theme.secondary};
  margin-top: ${pad}px;
`;

const Filters = styled.div`
  position: relative;
`;

const OptionSub = styled.div`
  position: absolute;
  top: 100%;
  left: -115px;
  width: 230px;
  padding: ${pad}px;
  margin-top: ${pad}px;
  border: ${border} solid ${({theme}) => theme.primary};
  border-radius: ${radius};
  color: ${({theme}) => theme.secondary};
  background: ${({theme}) => theme.tertiary};
  box-shadow: ${shadow};
  z-index: ${z("top")};

  ${bp(2)} {
    left: 0;
  }
`;

const ApplyFilter = styled(Button)`
  ${voice.quiet};
  width: fit-content;
  margin-right: ${pad / 2}px;
  padding: ${pad / 2}px;
  display: inline-flex;

  svg {
    fill: ${({theme}) => theme.tertiary};
    align-self: center;
  }

  &:last-child {
    margin-right: 0;
  }
`;

const DetailWrapper = styled.div`
  margin: ${pad * 1.5}px 0 0;

  > div {
    margin: 0;
  }
`;

const View = styled.button`
  width: fit-content;
  display: flex;
  align-items: center;
  color: ${({theme}) => theme.secondary};

  ${({active, theme}) =>
    active &&
    css`
      color: ${theme.primary};
    `}

  &:hover {
    text-decoration: underline;
  }
`;

const Icon = styled(FontAwesomeIcon)`
  padding: ${pad}px 0 ${pad}px ${pad / 2}px;
  fill: ${({color, theme}) => color || theme.primary};
`;

const SuccessButton = styled(Button)`
  color: ${({theme}) => theme.success};
`;

const WarningButton = styled(Button)`
  color: ${({theme}) => theme.error};
`;

const Edit = styled.button`
  position: absolute;
  top: ${pad / 2}px;
  right: ${pad}px;
  width: fit-content;
  z-index: ${z("base") + 1};

  svg {
    fill: ${({theme}) => theme.secondary};
    padding: ${pad / 2}px 0 ${pad}px ${pad}px;
  }
`;

const AddressEdit = styled(Edit)`
  right: ${pad * 3.5}px;
`;

export default FacilityBuilder;
